import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { SupportedGames } from "@/types/game";
import { DeckItem } from "@/types/deck";
import { useMemo, useCallback } from "react";
import { GAMES, createInitialStoreState } from "@/games";

// Re-export for convenience
export type { DeckItem };

// =============================================================================
// 1. DECK STORE - Manages deck contents and card order
// =============================================================================

/**
 * Stable empty results for the selectors below.
 *
 * A zustand selector must return the same reference when nothing changed. The
 * per-game maps are persisted to localStorage, so a browser that stored them
 * before a game was added has no key for it -- and a `|| []` literal in the
 * selector then hands back a fresh array on every render, which zustand reads
 * as a change and loops ("The result of getSnapshot should be cached to avoid
 * an infinite loop"). Any newly registered game hit this; Palworld is just the
 * first to have a deck builder.
 */
export const EMPTY_DECK_ITEMS: DeckItem[] = [];
const EMPTY_CARD_ORDER_IDS: string[] = [];

interface DeckStoreState {
  // Current active game
  currentGame: SupportedGames;

  // Deck data per game (single source of truth)
  decks: Record<SupportedGames, DeckItem[]>;

  // Card order for UI display per game
  cardOrderIds: Record<SupportedGames, string[]>;

  // Actions
  setCurrentGame: (game: SupportedGames) => void;
  addCard: (game: SupportedGames, cardId: string, count?: number) => void;
  setCard: (game: SupportedGames, cardId: string, count: number) => void;
  clearDeck: (game: SupportedGames) => void;
  setDeck: (game: SupportedGames, items: DeckItem[]) => void;
  setCardOrderIds: (game: SupportedGames, ids: string[]) => void;

  // Getters
  getDeck: (game: SupportedGames) => DeckItem[];
  getDeckCount: (game: SupportedGames) => number;
  getCardCount: (game: SupportedGames, cardId: string) => number;
  getDeckCounts: (game: SupportedGames) => Map<string, number>;
  getDeckString: (game: SupportedGames, basic?: boolean) => string;
}

export const useDeckStore = create<DeckStoreState>()(
  persist(
    (set, get) => ({
      currentGame: "one-piece", // Default game
      decks: createInitialStoreState<any[]>([]),
      cardOrderIds: createInitialStoreState<any[]>([]),

      setCurrentGame: (game) => {
        set({ currentGame: game });
      },

      addCard: (game, cardId, count = 1) => {
        set((state) => {
          const currentDeck = state.decks[game] || [];
          const existingIndex = currentDeck.findIndex(
            (item) => item.id === cardId,
          );
          let newItems: DeckItem[];

          if (existingIndex !== -1) {
            // Update existing card count
            const newCount = currentDeck[existingIndex].count + count;
            if (newCount <= 0) {
              // Remove card if count goes to 0 or below
              newItems = currentDeck.filter((_, i) => i !== existingIndex);
            } else {
              // Update count
              newItems = currentDeck.map((item, i) =>
                i === existingIndex ? { ...item, count: newCount } : item,
              );
            }
          } else {
            // Add new card
            newItems = [...currentDeck, { id: cardId, count }];
          }

          return {
            decks: {
              ...state.decks,
              [game]: newItems,
            },
          };
        });
      },

      setCard: (game, cardId, count) => {
        set((state) => {
          const currentDeck = state.decks[game] || [];
          const existingIndex = currentDeck.findIndex(
            (item) => item.id === cardId,
          );
          let newItems: DeckItem[];

          if (count <= 0) {
            // Remove card
            if (existingIndex !== -1) {
              newItems = currentDeck.filter((_, i) => i !== existingIndex);
            } else {
              newItems = currentDeck;
            }
          } else {
            if (existingIndex !== -1) {
              // Update existing
              newItems = currentDeck.map((item, i) =>
                i === existingIndex ? { ...item, count } : item,
              );
            } else {
              // Add new
              newItems = [...currentDeck, { id: cardId, count }];
            }
          }

          return {
            decks: {
              ...state.decks,
              [game]: newItems,
            },
          };
        });
      },

      clearDeck: (game) => {
        set((state) => ({
          decks: {
            ...state.decks,
            [game]: [],
          },
          cardOrderIds: {
            ...state.cardOrderIds,
            [game]: [],
          },
        }));
      },

      setDeck: (game, items) => {
        set((state) => ({
          decks: {
            ...state.decks,
            [game]: items,
          },
        }));
      },

      setCardOrderIds: (game, ids) => {
        set((state) => ({
          cardOrderIds: {
            ...state.cardOrderIds,
            [game]: ids,
          },
        }));
      },

      getDeck: (game) => {
        return get().decks[game] || [];
      },

      getDeckCount: (game) => {
        const items = get().decks[game] || [];
        return items.reduce((sum, item) => sum + item.count, 0);
      },

      getCardCount: (game, cardId) => {
        const items = get().decks[game] || [];
        const item = items.find((i) => i.id === cardId);
        return item?.count || 0;
      },

      getDeckCounts: (game) => {
        const items = get().decks[game] || [];
        const counts = new Map<string, number>();
        items.forEach((item) => {
          counts.set(item.id, item.count);
        });
        return counts;
      },

      getDeckString: (game, basic = false) => {
        const items = get().decks[game] || [];
        return items
          .map((item: DeckItem) => {
            let cardId = item.id;
            // If basic mode, strip alternate art suffixes (_e0, _a0, _p0, etc.)
            if (basic && cardId.includes("_")) {
              const lastUnderscoreIndex = cardId.lastIndexOf("_");
              cardId = cardId.substring(0, lastUnderscoreIndex);
            }
            return `${item.count}x${cardId}`;
          })
          .join(";");
      },
    }),
    {
      name: "deck-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        decks: state.decks,
        cardOrderIds: state.cardOrderIds,
      }),
    },
  ),
);

// =============================================================================
// 2. FILTER STORE - Manages card database filters and search
// =============================================================================

interface FilterStoreState {
  // Current active game
  currentGame: SupportedGames;

  // Filter state per game
  filters: Record<SupportedGames, any>;

  // Search display (debounced from actual search filter)
  searchDisplay: string;

  // Actions
  setCurrentGame: (game: SupportedGames) => void;
  setFilter: (game: SupportedGames, filterId: string, value: any) => void;
  clearAllFilters: (game: SupportedGames, defaultFilters: any) => void;
  setFilters: (game: SupportedGames, filters: any) => void;
  setSearchDisplay: (search: string) => void;
}

export const useFilterStore = create<FilterStoreState>()(
  persist(
    (set, get) => ({
      currentGame: "one-piece",
      filters: createInitialStoreState<Record<string, any>>({}),
      searchDisplay: "",

      setCurrentGame: (game) => {
        set({ currentGame: game });
      },

      setFilter: (game, filterId, value) => {
        set((state) => ({
          filters: {
            ...state.filters,
            [game]: {
              ...state.filters[game],
              [filterId]: value,
            },
          },
        }));
      },

      clearAllFilters: (game, defaultFilters) => {
        set((state) => ({
          filters: {
            ...state.filters,
            [game]: defaultFilters,
          },
        }));
      },

      setFilters: (game, filters) => {
        set((state) => ({
          filters: {
            ...state.filters,
            [game]: filters,
          },
        }));
      },

      setSearchDisplay: (search) => {
        set({ searchDisplay: search });
      },
    }),
    {
      name: "filter-storage",
      storage: createJSONStorage(() => localStorage, {
        // Custom serializer to handle Sets
        replacer: (key, value) => {
          if (value instanceof Set) {
            return { _type: "Set", values: Array.from(value) };
          }
          return value;
        },
        // Custom deserializer to restore Sets
        reviver: (key, value) => {
          if (
            value &&
            typeof value === "object" &&
            "_type" in value &&
            value._type === "Set" &&
            "values" in value &&
            Array.isArray(value.values)
          ) {
            return new Set(value.values);
          }
          return value;
        },
      }),
      partialize: (state) => ({
        filters: state.filters,
      }),
      // Validate and fix filters after rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Fix any corrupted filters that were serialized before the Set fix
          // Derived from the registry: this list used to be hand-written and
          // had fallen out of date (naruto was missing, so its filters were
          // never repaired after rehydration).
          const games = GAMES.map((g) => g.id) as SupportedGames[];
          games.forEach((game) => {
            const gameFilters = state.filters[game];
            if (gameFilters) {
              Object.keys(gameFilters).forEach((key) => {
                const value = gameFilters[key];
                // If value is an empty object {}, reset to empty Set
                if (
                  value &&
                  typeof value === "object" &&
                  !(value instanceof Set) &&
                  value.constructor === Object &&
                  Object.keys(value).length === 0
                ) {
                  gameFilters[key] = new Set();
                }
              });
            }
          });
        }
      },
    },
  ),
);

// =============================================================================
// 3. VIEW STORE - Manages UI state (layout, page, modal, etc.)
// =============================================================================

interface ViewStoreState {
  // Current active game
  currentGame: SupportedGames;

  // UI State
  layout: string;
  page: number;
  hoveredAACardId: string | null;

  // Modal state
  modalState: {
    isOpen: boolean;
    aaCardId: string | null;
    currentCardIndex: number;
  };

  // Actions
  setCurrentGame: (game: SupportedGames) => void;
  setLayout: (layout: string) => void;
  setPage: (page: number | ((prev: number) => number)) => void;
  setHoveredAACardId: (id: string | null) => void;
  openCardModal: (aaCardId: string, cardIndex: number) => void;
  closeCardModal: () => void;
  setModalCardIndex: (index: number) => void;
}

export const useViewStore = create<ViewStoreState>()(
  persist(
    (set, get) => ({
      currentGame: "one-piece",
      layout: "bottom",
      page: 1,
      hoveredAACardId: null,
      modalState: {
        isOpen: false,
        aaCardId: null,
        currentCardIndex: -1,
      },

      setCurrentGame: (game) => {
        set({ currentGame: game });
      },

      setLayout: (layout) => {
        set({ layout });
      },

      setPage: (page) => {
        if (typeof page === "function") {
          set((state) => ({ page: page(state.page) }));
        } else {
          set({ page });
        }
      },

      setHoveredAACardId: (id) => {
        set({ hoveredAACardId: id });
      },

      openCardModal: (aaCardId, cardIndex) => {
        set({
          modalState: {
            isOpen: true,
            aaCardId,
            currentCardIndex: cardIndex,
          },
        });
      },

      closeCardModal: () => {
        set({
          modalState: {
            isOpen: false,
            aaCardId: null,
            currentCardIndex: -1,
          },
        });
      },

      setModalCardIndex: (index) => {
        set((state) => ({
          modalState: {
            ...state.modalState,
            currentCardIndex: index,
          },
        }));
      },
    }),
    {
      name: "view-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        layout: state.layout,
      }),
    },
  ),
);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Helper function to import deck from URL string
export function importDeckString(game: SupportedGames, deckString: string) {
  const items: DeckItem[] = [];
  const cards = deckString.split(";");
  for (const card of cards) {
    if (card.split("x").length === 2) {
      const [countStr, cardId] = card.split("x");
      items.push({ id: cardId, count: parseInt(countStr) });
    }
  }
  useDeckStore.getState().setDeck(game, items);
}

// Helper to sync currentGame across all stores
export function setCurrentGameAll(game: SupportedGames) {
  useDeckStore.getState().setCurrentGame(game);
  useFilterStore.getState().setCurrentGame(game);
  useViewStore.getState().setCurrentGame(game);
}

// =============================================================================
// HOOKS - Backward compatible interface
// =============================================================================

// Current Game Hooks
export function useCurrentGame() {
  return useDeckStore((state) => state.currentGame);
}

export function useSetCurrentGame() {
  return useCallback((game: SupportedGames) => {
    setCurrentGameAll(game);
  }, []);
}

// Deck Hooks
export function useDeckItems() {
  const game = useDeckStore((state) => state.currentGame);
  return useDeckStore((state) => state.decks[game] || EMPTY_DECK_ITEMS);
}

export function useCardOrderIds() {
  const game = useDeckStore((state) => state.currentGame);
  return useDeckStore(
    (state) => state.cardOrderIds[game] || EMPTY_CARD_ORDER_IDS,
  );
}

export function useSetCardOrderIds() {
  return useCallback((ids: string[] | ((prev: string[]) => string[])) => {
    const state = useDeckStore.getState();
    const game = state.currentGame;

    if (typeof ids === "function") {
      const currentIds = state.cardOrderIds[game] || [];
      const newIds = ids(currentIds);
      state.setCardOrderIds(game, newIds);
    } else {
      state.setCardOrderIds(game, ids);
    }
  }, []);
}

export function useAddCard() {
  return useCallback((cardId: string, count = 1) => {
    const game = useDeckStore.getState().currentGame;
    useDeckStore.getState().addCard(game, cardId, count);
  }, []);
}

export function useSetCard() {
  return useCallback((cardId: string, count: number) => {
    const game = useDeckStore.getState().currentGame;
    useDeckStore.getState().setCard(game, cardId, count);
  }, []);
}

export function useClearNewDeck() {
  const clearDeck = useDeckStore((state) => state.clearDeck);
  const game = useDeckStore((state) => state.currentGame);
  return useMemo(() => {
    return () => clearDeck(game);
  }, [game, clearDeck]);
}

export function useGetCount() {
  const game = useDeckStore((state) => state.currentGame);
  const getDeckCount = useDeckStore((state) => state.getDeckCount);
  return useMemo(() => {
    return () => getDeckCount(game);
  }, [game, getDeckCount]);
}

export function useCopyDeck() {
  const game = useDeckStore((state) => state.currentGame);
  const getDeckString = useDeckStore((state) => state.getDeckString);
  return useMemo(() => {
    return (basic = false) => {
      const deckString = getDeckString(game, basic);
      navigator.clipboard.writeText(deckString);
    };
  }, [game, getDeckString]);
}

export function useGetDecklist() {
  const game = useDeckStore((state) => state.currentGame);
  const getDeckString = useDeckStore((state) => state.getDeckString);
  return useMemo(() => {
    return (basic = false) => getDeckString(game, basic);
  }, [game, getDeckString]);
}

export function useGetLeaderId(handler: any) {
  const game = useDeckStore((state) => state.currentGame);
  const decks = useDeckStore((state) => state.decks);
  return useMemo(() => {
    return () => {
      const items = decks[game] || [];
      for (const item of items) {
        const cardData = handler.getCardData(item.id);
        if (cardData && handler.isLeader?.(cardData)) {
          return item.id;
        }
      }
      return undefined;
    };
  }, [game, handler, decks]);
}

export function useDeckCounts() {
  const game = useDeckStore((state) => state.currentGame);
  const deckItems = useDeckStore(
    (state) => state.decks[game] || EMPTY_DECK_ITEMS,
  );

  // Compute counts from deck items in useMemo to avoid creating new Map on every render
  return useMemo(() => {
    const counts = new Map<string, number>();
    deckItems.forEach((item) => {
      counts.set(item.id, item.count);
    });
    return counts;
  }, [deckItems]);
}

// View State Hooks
export function useLayout() {
  return useViewStore((state) => state.layout);
}

export function useSetLayout() {
  return useViewStore((state) => state.setLayout);
}

export function usePage() {
  return useViewStore((state) => state.page);
}

export function useSetPage() {
  return useViewStore((state) => state.setPage);
}

export function useHoveredAACardId() {
  return useViewStore((state) => state.hoveredAACardId);
}

export function useSetHoveredAACardId() {
  return useViewStore((state) => state.setHoveredAACardId);
}

// Modal State Hooks
export function useModalState() {
  return useViewStore((state) => state.modalState);
}

export function useOpenCardModal() {
  return useViewStore((state) => state.openCardModal);
}

export function useCloseCardModal() {
  return useViewStore((state) => state.closeCardModal);
}

export function useSetModalCardIndex() {
  return useViewStore((state) => state.setModalCardIndex);
}

// Filter Hooks (read from currentGame)
export function useFilters() {
  // Subscribe to both filters and currentGame to ensure re-renders
  const currentGame = useFilterStore((state) => state.currentGame);
  const gameFilters = useFilterStore((state) => state.filters[currentGame]);
  return gameFilters || {};
}

export function useSetFilter() {
  // Return a function that reads currentGame from store at call time
  return useCallback((filterId: string, value: any) => {
    const state = useFilterStore.getState();
    state.setFilter(state.currentGame, filterId, value);
  }, []);
}

export function useClearAllFilters() {
  // Return a function that reads currentGame from store at call time
  return useCallback((defaultFilters: any) => {
    const state = useFilterStore.getState();
    state.clearAllFilters(state.currentGame, defaultFilters);
  }, []);
}

export function useSetFilters() {
  // Return a function that reads currentGame from store at call time
  return useCallback((filters: any) => {
    const state = useFilterStore.getState();
    state.setFilters(state.currentGame, filters);
  }, []);
}

export function useSearchDisplay() {
  return useFilterStore((state) => state.searchDisplay);
}

export function useSetSearchDisplay() {
  return useFilterStore((state) => state.setSearchDisplay);
}
